import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageSection, Surface, relativeTime } from "@/components/shared";
import StatusBadge from "@/components/StatusBadge";
import { SYNC } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  RefreshCw, Database, Zap, CheckCircle2, Loader2, Clock,
  Building2, Package, AlertCircle, TrendingUp, X, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import WingsConfigCard from "@/components/WingsConfigCard";

export default function SyncMonitor() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState({ full: false, inc: false, fm: false, master: false, abort: false });
  const [confirmFull, setConfirmFull] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await api.get("/sync/logs?lines=300");
      setLogs(res.data?.logs || "(empty)");
    } catch (err) {
      setLogs(`Failed to fetch logs: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showLogs) fetchLogs();
  }, [showLogs, fetchLogs]);
  const [now, setNow] = useState(Date.now());
  const [fmDays, setFmDays] = useState(30);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([
      api.get("/sync/settings"),
      api.get("/sync/history", { params: { limit: 30 } }),
    ]);
    setSettings(s.data);
    setHistory(h.data.filter((row) => row.entity === "all"));
  }, []);

  useEffect(() => {
    load();
    // Poll more often while a sync is running
    const id = setInterval(load, 5000);
    const ticker = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(id); clearInterval(ticker); };
  }, [load]);

  const runFullSync = async () => {
    setConfirmFull(false);
    setBusy((b) => ({ ...b, full: true }));
    try {
      await api.post("/sync/full-sync");
      toast.success("Full Sync started — this can take a few minutes.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Full sync failed");
    } finally {
      setBusy((b) => ({ ...b, full: false }));
    }
  };

  const runMasterSync = async () => {
    setBusy((b) => ({ ...b, master: true }));
    try {
      await api.post("/sync/master/run");
      toast.success("Master Sync started — pulling MasterDetails from Wings.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Master sync failed");
    } finally {
      setBusy((b) => ({ ...b, master: false }));
    }
  };

  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateMode, setReactivateMode] = useState("with_stock"); // "with_stock" | "all"
  const [reactivating, setReactivating] = useState(false);

  const runReactivate = async () => {
    setReactivating(true);
    try {
      const onlyWithStock = reactivateMode === "with_stock";
      const res = await api.post(
        `/sync/master/reactivate-false-positives?only_with_stock=${onlyWithStock}`,
      );
      const { candidates = 0, reactivated = 0 } = res.data || {};
      if (candidates === 0) {
        toast.success("Nothing to reactivate — no hidden products found.");
      } else {
        toast.success(
          `Reactivated ${reactivated.toLocaleString("en-IN")} product(s) — Stock Monitoring will show them right away.`,
        );
      }
      setReactivateOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reactivation failed");
    } finally {
      setReactivating(false);
    }
  };

  const toggleIncremental = async (enabled) => {
    setBusy((b) => ({ ...b, inc: true }));
    try {
      const res = await api.post("/sync/incremental/toggle", { enabled });
      setSettings({ ...settings, ...res.data });
      toast.success(`Incremental sync ${enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to toggle");
    } finally {
      setBusy((b) => ({ ...b, inc: false }));
    }
  };

  const runIncrementalNow = async () => {
    setBusy((b) => ({ ...b, inc: true }));
    try {
      await api.post("/sync/incremental/run");
      toast.success("Incremental sync started");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incremental sync failed");
    } finally {
      setBusy((b) => ({ ...b, inc: false }));
    }
  };

  const refreshFastMoving = async () => {
    setBusy((b) => ({ ...b, fm: true }));
    try {
      const res = await api.post(`/sync/fast-moving/refresh?days=${fmDays}`);
      toast.success(`Fast-moving refreshed · ${res.data.rows_updated} rows updated over ${fmDays} days`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fast-moving refresh failed");
    } finally {
      setBusy((b) => ({ ...b, fm: false }));
    }
  };

  const abortSync = async () => {
    if (!running) return;
    if (!window.confirm(`Abort the running ${running.type} sync?`)) return;
    setBusy((b) => ({ ...b, abort: true }));
    try {
      const res = await api.post("/sync/abort");
      toast.success(`Aborted ${res.data.type} sync`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Abort failed");
    } finally {
      setBusy((b) => ({ ...b, abort: false }));
    }
  };

  if (!settings) {
    return <div className="h-64 bg-white border border-gray-200 rounded-md pulse-soft" />;
  }

  const lastFullAt = settings.last_full_sync_at;
  const lastIncAt = settings.last_incremental_sync_at;
  const fullDone = !!lastFullAt;
  const intervalMs = (settings.incremental_interval_minutes || 15) * 60 * 1000;
  const running = settings.running;
  const runningKind = running?.type;

  const runningElapsed = running ? Math.floor((Date.now() - new Date(running.started_at).getTime()) / 1000) : 0;

  let nextInMs = null;
  if (settings.incremental_enabled && lastIncAt) {
    const lastMs = new Date(lastIncAt).getTime();
    nextInMs = Math.max(0, lastMs + intervalMs - now);
  }
  const formatCountdown = (ms) => {
    if (ms == null) return "—";
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}m ${s.toString().padStart(2, "0")}s`;
  };

  return (
    <div className="space-y-8" data-testid="sync-monitor-container">
      <WingsConfigCard />

      {/* ⚠️ Recent failure banner — surfaces the actual error so user doesn't need to dig */}
      {(() => {
        const failed = [settings.last_full_run, settings.last_master_run, settings.last_incremental_run]
          .filter(r => r && r.status === "failed" && r.error);
        if (!running && failed.length > 0) {
          const latest = failed.sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""))[0];
          const label = latest.type === "full" ? "Full Sync" : latest.type === "master" ? "Master Sync" : "Incremental Sync";
          return (
            <div className="rounded-md bg-red-50 border-2 border-red-300 px-5 py-4 shadow-sm" data-testid="sync-failure-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-red-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-red-900">
                    Last {label} failed — {relativeTime(latest.completed_at)}
                  </p>
                  <p className="text-xs text-red-800 mt-1 font-mono leading-relaxed break-words" data-testid="sync-failure-error">
                    {latest.error}
                  </p>
                  {latest.progress?.phase && (
                    <p className="text-[11px] text-red-700 mt-1">
                      Failed at phase: <span className="font-mono font-semibold">{latest.progress.phase}</span>
                      {latest.progress?.message && ` · last message: "${latest.progress.message}"`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { navigator.clipboard.writeText(latest.error); toast.success("Error copied"); }}
                    className="h-8 border-red-300 text-red-700 hover:bg-red-100"
                    data-testid="sync-failure-copy"
                  >
                    Copy error
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowLogs(true); }}
                    className="h-8 border-red-300 text-red-700 hover:bg-red-100"
                    data-testid="sync-failure-view-logs"
                  >
                    View logs
                  </Button>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Running indicator */}
      {running && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-5 py-4" data-testid="sync-running-banner">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-blue-900">
                {runningKind === "full"
                  ? "Full Sync"
                  : runningKind === "master"
                  ? "Master Sync"
                  : "Incremental Sync"} in progress…
              </p>
              <p className="text-xs text-blue-800 mt-0.5 truncate" data-testid="sync-progress-message">
                {running.progress?.message || "Fetching from Wings…"}
              </p>
              {running.progress?.endpoint && (
                <p className="text-[10px] font-mono text-blue-700/70 mt-0.5 truncate" data-testid="sync-progress-endpoint" title={running.progress.endpoint}>
                  {running.progress.endpoint}
                </p>
              )}
              <p className="text-[11px] text-blue-700/80 mt-0.5">
                Running for {Math.floor(runningElapsed / 60)}m {runningElapsed % 60}s ·
                triggered by {running.triggered_by}
                {typeof running.progress?.percent === "number" && ` · ${running.progress.percent}%`}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={abortSync}
              disabled={busy.abort}
              className="h-8 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 shrink-0"
              data-testid="sync-abort-button"
            >
              {busy.abort ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Aborting…</>
              ) : (
                <><X className="h-3.5 w-3.5 mr-1.5" /> Abort</>
              )}
            </Button>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 text-blue-900 text-xs font-semibold shrink-0">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse" />
              Running
            </span>
          </div>
          {/* Progress bar */}
          {typeof running.progress?.percent === "number" && (
            <div className="mt-3 h-1.5 bg-blue-100 rounded-full overflow-hidden" data-testid="sync-progress-bar">
              <div
                className="h-full bg-blue-600 transition-all duration-500 ease-out"
                style={{ width: `${Math.min(100, Math.max(0, running.progress.percent))}%` }}
              />
            </div>
          )}
        </div>
      )}
      {/* Three main cards: Master · Full · Incremental */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Master Sync Card */}
        <div className="bg-white border border-gray-200 rounded-md p-6 flex flex-col gap-4" data-testid={SYNC.entityCard("master")}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-md bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center shadow-sm">
                <BookOpen className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-gray-900 text-lg">Master Sync</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Refreshes the product catalogue from <span className="font-mono">MasterDetails</span> — adds new
                  products, marks obsolete ones inactive.
                </p>
              </div>
            </div>
            <StatusBadge variant={settings.last_master_run ? "success" : "warning"}>
              {settings.last_master_run ? "Synced" : "Never run"}
            </StatusBadge>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Last Run</p>
              <p className="font-heading text-base font-bold text-gray-900">
                {settings.last_master_sync_at ? relativeTime(settings.last_master_sync_at) : "—"}
              </p>
            </div>
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Products</p>
              <p className="font-heading text-base font-bold text-gray-900">
                {settings.last_master_run
                  ? (settings.last_master_run.stats?.products_upserted || 0).toLocaleString("en-IN")
                  : "—"}
              </p>
            </div>
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 leading-snug">
            <AlertCircle className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Run this whenever new products are added in Wings, or nightly to keep product metadata (name, MRP, ActiveStatus) fresh.
          </div>

          {settings.last_master_run?.stats?.master_diag?.deactivation_skipped_reason && (
            <div
              className="rounded-md bg-yellow-50 border border-yellow-300 px-3 py-2 text-xs text-yellow-900 leading-snug"
              data-testid="master-deactivation-skipped-banner"
            >
              <AlertCircle className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
              <span className="font-medium">Last run skipped deactivation:</span>{" "}
              {settings.last_master_run.stats.master_diag.deactivation_skipped_reason}
            </div>
          )}

          <Button
            onClick={runMasterSync}
            disabled={busy.master || !!running}
            className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="sync-trigger-master"
          >
            {busy.master || runningKind === "master" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
            ) : (
              <><BookOpen className="h-4 w-4 mr-2" /> Run Master Sync Now</>
            )}
          </Button>

          <Button
            variant="outline"
            onClick={() => { setReactivateMode("with_stock"); setReactivateOpen(true); }}
            disabled={!!running || reactivating}
            className="w-full h-9 text-amber-700 border-amber-300 hover:bg-amber-50 text-sm"
            data-testid="sync-master-reactivate-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-2" /> Reactivate hidden products…
          </Button>
        </div>

        {/* Full Sync Card */}
        <div className="bg-white border border-gray-200 rounded-md p-6 flex flex-col gap-4" data-testid={SYNC.entityCard("full")}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-md bg-gradient-to-br from-[#9B1C1C] to-[#7F1D1D] text-white flex items-center justify-center shadow-sm">
                <Database className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-gray-900 text-lg">Full Sync</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Rebuilds stock from <span className="font-mono">StockDetails</span> — absolute quantities across
                  all branches, matched by product code.
                </p>
              </div>
            </div>
            <StatusBadge variant={fullDone ? "success" : "warning"}>
              {fullDone ? "Synced" : "Never run"}
            </StatusBadge>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Last Run</p>
              <p className="font-heading text-base font-bold text-gray-900">{fullDone ? relativeTime(lastFullAt) : "—"}</p>
            </div>
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Last Items</p>
              <p className="font-heading text-base font-bold text-gray-900">
                {settings.last_full_run ? settings.last_full_run.items_synced.toLocaleString("en-IN") : "—"}
              </p>
            </div>
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 leading-snug">
            <AlertCircle className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Running a Full Sync will automatically <b>turn OFF</b> the Incremental loop. Switch it back on once the
            Full Sync finishes.
          </div>

          <Button
            onClick={() => setConfirmFull(true)}
            disabled={busy.full || !!running}
            className="w-full h-11 bg-[#9B1C1C] hover:bg-[#7F1D1D]"
            data-testid="sync-trigger-full"
          >
            {busy.full || runningKind === "full" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
            ) : (
              <><Database className="h-4 w-4 mr-2" /> Run Full Sync Now</>
            )}
          </Button>
        </div>

        {/* Incremental Sync Card */}
        <div className="bg-white border border-gray-200 rounded-md p-6 flex flex-col gap-4" data-testid={SYNC.entityCard("incremental")}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`h-11 w-11 rounded-md flex items-center justify-center shadow-sm transition-colors ${
                settings.incremental_enabled
                  ? "bg-gradient-to-br from-emerald-500 to-emerald-700 text-white"
                  : "bg-gray-100 text-gray-500"
              }`}>
                <Zap className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-gray-900 text-lg">Incremental Sync</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Every {settings.incremental_interval_minutes} minutes — pulls only what changed since the last sync.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${settings.incremental_enabled ? "text-emerald-700" : "text-gray-400"}`}>
                {settings.incremental_enabled ? "ON" : "OFF"}
              </span>
              <Switch
                checked={settings.incremental_enabled}
                disabled={!fullDone || busy.inc}
                onCheckedChange={(c) => toggleIncremental(c === true)}
                data-testid="sync-incremental-toggle"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Last Run</p>
              <p className="font-heading text-base font-bold text-gray-900">
                {lastIncAt ? relativeTime(lastIncAt) : "—"}
              </p>
            </div>
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                {settings.incremental_enabled ? "Next Run In" : "Status"}
              </p>
              <p className="font-heading text-base font-bold text-gray-900">
                {settings.incremental_enabled ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-emerald-700" />
                    <span className="font-mono">{formatCountdown(nextInMs)}</span>
                  </span>
                ) : (
                  "Paused"
                )}
              </p>
            </div>
          </div>

          {!fullDone ? (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 leading-snug">
              <AlertCircle className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
              Run a Full Sync first — the toggle activates only after the initial sync is complete.
            </div>
          ) : (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 leading-snug">
              <CheckCircle2 className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
              {settings.incremental_enabled
                ? "Loop is running. Will continue every 15 minutes until you turn it off or run another Full Sync."
                : "Toggle ON to activate the 15-minute delta loop."}
            </div>
          )}

          <Button
            variant="outline"
            onClick={runIncrementalNow}
            disabled={!fullDone || busy.inc || !!running}
            className="w-full h-11"
            data-testid="sync-incremental-run-now"
          >
            {busy.inc || runningKind === "incremental" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-2" /> Run Incremental Now</>
            )}
          </Button>
        </div>
      </div>

      {/* Fast-Moving Analytics Card */}
      <div className="bg-white border border-gray-200 rounded-md p-6 flex flex-col gap-4" data-testid="sync-fast-moving-card">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-md bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center shadow-sm">
              <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-gray-900 text-lg">Fast-Moving Refresh</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Pulls per-day Issue Quantity from Wings <span className="font-mono text-[11px]">StockDetailsByDate</span> and updates the &quot;sold last N days&quot; metric used by Procurement.
              </p>
            </div>
          </div>
          {settings.last_fast_moving_at && (
            <StatusBadge variant="success">
              {settings.fast_moving_window_days || 30}-day window
            </StatusBadge>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Last Run</p>
            <p className="font-heading text-base font-bold text-gray-900">
              {settings.last_fast_moving_at ? relativeTime(settings.last_fast_moving_at) : "—"}
            </p>
          </div>
          <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Lookback (Days)</p>
            <div className="flex items-center gap-2 mt-0.5">
              <Input
                type="number"
                min={1}
                max={365}
                value={fmDays}
                onChange={(e) => setFmDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 30)))}
                className="h-8 w-20 font-mono text-base font-bold"
                data-testid="sync-fast-moving-days"
              />
              <span className="text-xs text-gray-500">days back</span>
            </div>
          </div>
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 leading-snug">
          <AlertCircle className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
          This makes {fmDays} sequential API calls (one per day). Run it after a Full Sync, then whenever you want fresh velocity numbers — not part of the 15-minute loop.
        </div>

        <Button
          onClick={refreshFastMoving}
          disabled={!fullDone || busy.fm || !!running}
          variant="outline"
          className="w-full h-11 border-amber-300 text-amber-900 hover:bg-amber-50"
          data-testid="sync-fast-moving-run"
        >
          {busy.fm ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching {fmDays} days…</>
          ) : (
            <><TrendingUp className="h-4 w-4 mr-2" /> Refresh Fast-Moving ({fmDays} days)</>
          )}
        </Button>
      </div>

      {/* Entities covered by both APIs */}
      <PageSection
        title="What the Wings APIs Cover"
        description="Wings v2 exposes three endpoints — full sync uses two, fast-moving uses the third."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Building2, label: "Branches & Suppliers", description: "Auto-derived from StockDetails (Branch + Category columns)" },
            { icon: Package, label: "Products & Categories", description: "MasterDetails: Name, ProductCode, Node, Group, MRP" },
            { icon: TrendingUp, label: "Fast-Moving Stats", description: "StockDetailsByDate: daily Issue Quantities aggregated per product" },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-white border border-gray-200 rounded-md p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-red-50 text-[#9B1C1C] flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading font-semibold text-gray-900">{c.label}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">{c.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </PageSection>

      {/* History */}
      <PageSection title="Sync History" description="Latest 30 runs (Full + Incremental)">
        <Surface padded={false}>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Trigger</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Completed</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b border-gray-100 hover:bg-gray-50/60">
                  <td className="px-6 py-3">
                    {h.type === "master" ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800">
                        <BookOpen className="h-3 w-3 mr-1" /> Master
                      </span>
                    ) : h.type === "full" ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700">
                        <Database className="h-3 w-3 mr-1" /> Full
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                        <Zap className="h-3 w-3 mr-1" /> Incremental
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700 capitalize">{h.triggered_by || "manual"}</td>
                  <td className="px-4 py-3">
                    {h.status === "success" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> success
                      </span>
                    ) : h.status === "running" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-700">
                        <Loader2 className="h-3 w-3 animate-spin" /> running
                      </span>
                    ) : (
                      <div className="flex flex-col gap-1" data-testid="sync-history-failed-cell">
                        <span className="inline-flex items-center gap-1 text-xs text-red-700 font-semibold">
                          <AlertCircle className="h-3 w-3" /> failed
                        </span>
                        {h.error && (
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(h.error); toast.success("Error copied to clipboard"); }}
                            className="text-[11px] text-red-700 hover:text-red-900 underline text-left leading-snug break-all max-w-md"
                            title="Click to copy full error"
                            data-testid={`sync-error-text-${h.id}`}
                          >
                            {h.error.length > 180 ? h.error.slice(0, 180) + "…" : h.error}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{(h.items_synced || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-600">{h.duration_seconds}s</td>
                  <td className="px-6 py-3 text-right text-gray-600 text-xs">{h.completed_at ? relativeTime(h.completed_at) : "—"}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">No sync runs yet — start with a Full Sync.</td></tr>
              )}
            </tbody>
          </table>
        </Surface>
      </PageSection>

      {/* Full Sync confirmation */}
      <AlertDialog open={confirmFull} onOpenChange={setConfirmFull}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run Full Sync now?</AlertDialogTitle>
            <AlertDialogDescription>
              This will pull the entire catalogue from Wings and replace our local cache. It also
              <b> turns OFF the Incremental Sync loop</b> — you&apos;ll need to switch it back on once the Full Sync
              completes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runFullSync}
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="sync-confirm-full"
            >
              Yes, run Full Sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Backend logs viewer */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-4xl" data-testid="sync-logs-dialog">
          <DialogHeader>
            <DialogTitle>Backend Sync Logs — last 300 lines</DialogTitle>
            <DialogDescription>
              Recent sync-related entries from the backend logs. Use this to diagnose failures — the exact phase and
              exception class name is captured.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 -mt-2">
            <p className="text-xs text-gray-500">Log path: <span className="font-mono">/var/log/supervisor/backend.err.log</span></p>
            <Button variant="outline" size="sm" onClick={fetchLogs} disabled={logsLoading} className="h-8" data-testid="sync-logs-refresh">
              {logsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Refresh</span>
            </Button>
          </div>
          <pre className="bg-gray-900 text-emerald-300 text-[11px] font-mono p-4 rounded max-h-[60vh] overflow-auto whitespace-pre-wrap break-all" data-testid="sync-logs-content">
{logs || (logsLoading ? "Loading…" : "(click Refresh)")}
          </pre>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(logs); toast.success("Logs copied"); }} data-testid="sync-logs-copy">
              Copy all
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <AlertDialogContent data-testid="master-reactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate products hidden by Master Sync?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Wings' MasterDetails API sometimes returns an inconsistent subset of
                  the catalogue between calls. When that happens, the "absent from
                  master" auto-deactivation flips real products to inactive and they
                  disappear from Stock Monitoring. Use this action to bring them back.
                </p>
                <div className="space-y-2 pt-1">
                  <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50">
                    <input
                      type="radio" name="reactivate-mode" value="with_stock"
                      checked={reactivateMode === "with_stock"}
                      onChange={() => setReactivateMode("with_stock")}
                      data-testid="reactivate-mode-with-stock"
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Only with stock activity (safer)</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Reactivates only products that still have an inventory row from
                        Wings' Full Sync — proof they exist in Wings today.
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50">
                    <input
                      type="radio" name="reactivate-mode" value="all"
                      checked={reactivateMode === "all"}
                      onChange={() => setReactivateMode("all")}
                      data-testid="reactivate-mode-all"
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-gray-900">All Master-Sync false positives</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Reactivates every row deactivated by Master Sync, even those with
                        no current stock. Use after a confirmed Wings glitch.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reactivating} data-testid="reactivate-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runReactivate}
              disabled={reactivating}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reactivate-confirm-btn"
            >
              {reactivating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reactivating…</>
              ) : (
                <>Reactivate now</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
